'use client'
import classNames from 'classnames'
import { useRouter } from 'next/navigation'

import { useParkinglotTicketsViewModel } from '@/components/detail/components/parkinglot/components/tickets/list/viewModel'

import exploreEvents from '@/utils/gaEvents/exploreEvents'

import type { ParkinglotDetail, TicketsItem } from '@/api/server/detail/dto/detailDto'
import ShowAllButton from '@/shared/components/button/ShowAllButton'
import Icons from '@/shared/components/icon/Icons'
import NudgeBanner from '@/shared/components/NudgeBanner'
import { Text, Caption, Tint } from '@/shared/components/typography'
import useHash from '@/shared/hooks/useHash'

import { SHEET_STATE_TYPE } from '@/enums/detail'
import { HASH_KEY_TYPE } from '@/enums/map'

interface TicketsListViewProps {
  pin: ParkinglotDetail
  tickets: TicketsItem[]
}

const TicketsListView = ({ pin, tickets }: TicketsListViewProps) => {
  const router = useRouter()
  const { getHashAll, getHashByKey } = useHash()
  const { states, setters } = useParkinglotTicketsViewModel({ tickets })

  const getTicketDescription = (ticket: TicketsItem) => {
    if (ticket.isOpen || Number(ticket.couponTypeGroup) === 10000) {
      return `${ticket.usingDateLabel} ${ticket.usingTimeLabel} 사이 주차 가능`
    }
    if (!ticket.isOpen || ticket.isSoldOut) {
      return `${ticket.nextTimeLabel}`
    }
  }

  const onMoveToTicketDetail = (couponSeq: number) => {
    exploreEvents.partner.onClickTicketCard(pin.seq, pin.basic.latitude, pin.basic.longitude)
    const hash = getHashAll()
    router.push(`/t/${couponSeq}${hash}`)
  }

  const isFullSheet = getHashByKey(HASH_KEY_TYPE.SHEET) === SHEET_STATE_TYPE.VISIBLE

  return (
    <div className={`relative flex flex-col items-center justify-center gap-[8px] px-[16px]`}>
      {isFullSheet && <NudgeBanner className={`mb-1`} />}
      {states.parkinglotTickets.map((ticket) => (
        <div key={ticket.couponSeq} className="w-full">
          <div
            className={`border-50 relative flex h-[90px] w-full cursor-pointer rounded-[8px] border-050
      before:absolute before:top-0 before:h-full before:w-2 before:rounded-l-[8px] marker:before:content-[''] ${
        ticket.isOpen ? 'bg-white before:bg-[#0078FF]' : 'bg-020 before:bg-[#DEE4E9]'
      }`}
            onClick={() => onMoveToTicketDetail(ticket.couponSeq)}
          >
            <div className="flex h-full w-full items-center justify-between py-[10px] pl-6 pr-4">
              <div className="flex flex-col gap-[2px]">
                <Caption
                  level="caption1"
                  weight="regular"
                  className={`${
                    ticket.isOpen ? 'text-text-090' : 'text-text-050'
                  } overflow-hidden text-ellipsis whitespace-nowrap`}
                >
                  {ticket.couponName}
                </Caption>
                <Text level="text2" weight="bold" className={`${ticket.isOpen ? 'text-text-090' : 'text-text-050'}`}>
                  {`${Number(ticket.price).toLocaleString()}원`}
                </Text>
                <div
                  className={`text-text-070 ${
                    ((ticket.isOpen && ticket.isSoldOut) || !ticket.isOpen) && `gap-[2px]`
                  } flex items-center `}
                >
                  <Tint
                    level="tint1"
                    weight={Number(ticket.couponTypeGroup) === 10000 && ticket.isSoldOut ? 'regular' : 'bold'}
                    className={classNames(`${ticket.isOpen && !ticket.isSoldOut && 'text-main-050'}
                      ${Number(ticket.couponTypeGroup) === 10000 && ticket.isSoldOut && 'text-text-050'}
                      } 
                    `)}
                  >
                    {ticket.isOpen ? '판매중' : ticket.isSoldOut ? '현재매진' : '판매예정'}
                  </Tint>
                  <Icons.BulletIcon />
                  <Tint
                    level="tint1"
                    weight="regular"
                    color={Number(ticket.couponTypeGroup) === 10000 && !ticket.isOpen ? 'text050' : 'text070'}
                    className={`inline-block overflow-hidden text-ellipsis whitespace-nowrap`}
                  >
                    {getTicketDescription(ticket)}
                  </Tint>
                </div>
              </div>

              <div className="flex items-center">
                <Caption level="caption2" weight="regular" className={`flex items-center`}>
                  {ticket.isOpen ? <Icons.ArrowOn /> : <Icons.ArrowOff />}
                </Caption>
              </div>
            </div>
          </div>
        </div>
      ))}
      {!states.isShowAllTickets && states.parkinglotTickets.length > 3 && (
        <ShowAllButton onClick={() => setters.setIsShowAllTickets(true)} />
      )}
    </div>
  )
}

export default TicketsListView
